import React from "react";
import { Link } from "react-router-dom";
import Seo from "../lib/Seo";
import { ArrowLeft, ShieldCheck, RotateCcw, FileText, AlertTriangle } from "lucide-react";
import { CONTACT_EMAIL, WHATSAPP_NUMBER } from "../lib/api";

/* -------------------------------------------------------- Content blocks */

const TODAY = new Date().toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
const WA_HREF = `https://wa.me/${WHATSAPP_NUMBER}`;

const PRIVACY = {
  intro: `At YourAstroHub, we respect your privacy and are committed to protecting your personal information.`,
  sections: [
    {
      heading: "Information We Collect",
      body: [
        "We may collect the following personal information when you interact with our services:",
        "• Name",
        "• Mobile Number",
        "• Email Address",
        "• Date of Birth",
        "• Time of Birth",
        "• Place of Birth",
        "• Payment Details (transaction references only)",
        "• Messages submitted through forms or WhatsApp",
      ],
    },
    {
      heading: "How We Use Your Information",
      body: [
        "We use your information only to:",
        "• Schedule astrology consultations.",
        "• Process course enrollments.",
        "• Provide personalized horoscope analysis.",
        "• Send study materials and updates.",
        "• Respond to customer inquiries.",
        "• Improve our services.",
      ],
    },
    {
      heading: "Data Protection",
      body: [
        "All personal information is stored securely and is never sold or shared with third parties except where required by law or trusted service providers (such as payment processors or email delivery services).",
      ],
    },
    {
      heading: "Cookies",
      body: [
        "The website may use cookies to improve user experience and analyze website traffic.",
      ],
    },
    {
      heading: "Contact",
      body: [
        `For privacy-related questions, please contact us through the Contact page or WhatsApp at +91 9289212630.`,
      ],
    },
  ],
};

const REFUND = {
  intro: null,
  sections: [
    {
      heading: "Consultation Services",
      body: [
        "Consultation fees are generally non-refundable once the appointment has been confirmed.",
        "If YourAstroHub is unable to conduct the consultation, the appointment will be rescheduled or a refund may be provided at our discretion.",
      ],
    },
    {
      heading: "Online Courses",
      body: [
        "Refund requests are accepted only before access to live classes or recorded course material has been provided.",
        "Once access to course content has been granted, no refunds will be issued except where required by applicable law.",
      ],
    },
    {
      heading: "Duplicate Payments",
      body: ["Duplicate or excess payments will be refunded after verification."],
    },
    {
      heading: "Cancellation & Rescheduling",
      body: ["Clients may request rescheduling by contacting us at least 24 hours before the scheduled consultation, subject to availability."],
    },
  ],
};

const TERMS = {
  intro: "By using YourAstroHub, you agree to the following:",
  sections: [
    {
      heading: "Your Agreement",
      body: [
        "• Information provided must be accurate.",
        "• Astrology consultations are intended for guidance and self-reflection.",
        "• Users are responsible for their own decisions and actions.",
        "• Course materials are for personal learning only and may not be copied, distributed, or resold without written permission.",
        "• Fees, schedules, and course content may be updated from time to time.",
        "• Unauthorized use of website content is prohibited.",
      ],
    },
  ],
};

const DISCLAIMER = {
  intro: `The consultations, reports, articles, remedies, and courses offered by YourAstroHub are based on traditional Vedic Astrology principles and are intended for educational and guidance purposes only.`,
  sections: [
    {
      heading: "Not a Substitute for Professional Advice",
      body: ["Astrology should not be considered a substitute for professional advice from qualified medical, legal, financial, or mental health professionals."],
    },
    {
      heading: "No Guarantee of Outcomes",
      body: ["We do not guarantee any specific outcome from consultations, remedies, or courses. Results may vary based on individual circumstances, effort, and other factors beyond our control."],
    },
    {
      heading: "Your Responsibility",
      body: ["By using this website and our services, you acknowledge that you are responsible for your own decisions and actions."],
    },
  ],
};

/* --------------------------------------------------------------- catalogue */
const DOCS = {
  privacy: {
    icon: ShieldCheck,
    path: "/privacy",
    heading: "Privacy Policy",
    subheading: "How YourAstroHub collects, uses and protects your information.",
    title: "Privacy Policy | YourAstroHub",
    description: "How YourAstroHub collects, uses, and protects your personal information — including birth details, contact info and consultation records.",
    body: PRIVACY,
  },
  refund: {
    icon: RotateCcw,
    path: "/refund-policy",
    heading: "Refund Policy",
    subheading: "Terms for consultation and course refunds at YourAstroHub.",
    title: "Refund Policy | YourAstroHub",
    description: "YourAstroHub refund policy for consultations and online courses — including cancellations, rescheduling, and duplicate payments.",
    body: REFUND,
  },
  terms: {
    icon: FileText,
    path: "/terms",
    heading: "Terms & Conditions",
    subheading: "The terms that govern the use of YourAstroHub services.",
    title: "Terms & Conditions | YourAstroHub",
    description: "The terms and conditions governing the use of the YourAstroHub website, consultations, and courses.",
    body: TERMS,
  },
  disclaimer: {
    icon: AlertTriangle,
    path: "/disclaimer",
    heading: "Disclaimer",
    subheading: "Important information about the nature of our astrological services.",
    title: "Disclaimer | YourAstroHub",
    description: "YourAstroHub disclaimer regarding the nature of astrological consultations, remedies, courses, and educational content.",
    body: DISCLAIMER,
  },
};

/* ------------------------------------------------------------- Component */

function Line({ children }) {
  const isBullet = typeof children === "string" && children.startsWith("• ");
  return isBullet ? (
    <li className="flex items-start gap-2.5 text-foreground/80">
      <span className="text-[#D4AF37] mt-1.5">◆</span>
      <span>{children.slice(2)}</span>
    </li>
  ) : (
    <p className="text-foreground/85 leading-relaxed">{children}</p>
  );
}

function SectionBlock({ heading, body }) {
  const bullets = body.filter((b) => typeof b === "string" && b.startsWith("• "));
  const paragraphs = body.filter((b) => typeof b !== "string" || !b.startsWith("• "));
  return (
    <section>
      <h2 className="font-serif text-2xl md:text-3xl leading-tight mb-3 text-foreground">
        <span className="inline-block align-middle w-8 gold-hairline mr-3" />{heading}
      </h2>
      <div className="space-y-3">
        {paragraphs.map((p, i) => <Line key={`p-${i}`}>{p}</Line>)}
        {bullets.length > 0 && <ul className="space-y-2">{bullets.map((b, i) => <Line key={`b-${i}`}>{b}</Line>)}</ul>}
      </div>
    </section>
  );
}

export default function LegalPage({ kind }) {
  const d = DOCS[kind];
  if (!d) return null;
  const Icon = d.icon;

  return (
    <div className="min-h-screen">
      <Seo
        title={d.title}
        description={d.description}
        path={d.path}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: d.heading, path: d.path },
        ]}
      />

      {/* HERO ---------------------------------------------------- */}
      <section className="relative overflow-hidden -mt-20 pt-32 md:pt-36 pb-16 md:pb-20 text-[#F5E8D8] bg-[#0B1F3A]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A] via-[#0B1F3A] to-[#062036]" />
        <div className="absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-[#0E9F6E]/12 blur-[120px]" />
        <div className="absolute bottom-0 right-0 w-[480px] h-[480px] rounded-full bg-[#D4AF37]/10 blur-[120px]" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-8">
          <nav aria-label="breadcrumb" className="text-[11px] tracking-[0.22em] uppercase text-[#F5E8D8]/60 mb-5">
            <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
            <span className="mx-2 text-[#D4AF37]">/</span>
            <span className="text-[#D4AF37]">{d.heading}</span>
          </nav>

          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40 shrink-0">
              <Icon className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div>
              <div className="eyebrow">Legal</div>
              <h1 className="font-serif text-4xl md:text-5xl font-light leading-tight mt-1">{d.heading}</h1>
            </div>
          </div>
          <p className="mt-5 text-[#F5E8D8]/75 max-w-2xl">{d.subheading}</p>
          <div className="mt-4 text-xs text-[#F5E8D8]/60">
            <span className="text-[#D4AF37] uppercase tracking-[0.22em] font-semibold">Effective Date:</span> {TODAY}
          </div>
        </div>
      </section>

      {/* BODY ---------------------------------------------------- */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-8 grid md:grid-cols-12 gap-10">
          {/* Sidebar with all four legal links */}
          <aside className="md:col-span-3">
            <div className="sticky top-28 rounded-3xl border border-foreground/10 p-5">
              <div className="eyebrow mb-4">Legal Pages</div>
              <ul className="space-y-1.5">
                {Object.entries(DOCS).map(([key, doc]) => {
                  const active = key === kind;
                  return (
                    <li key={key}>
                      <Link to={doc.path} data-testid={`legal-nav-${key}`}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                          active ? "bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40" : "hover:bg-foreground/5 text-foreground/75"
                        }`}>
                        <doc.icon className="w-4 h-4 shrink-0" />
                        <span>{doc.heading}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
              <Link to="/" className="mt-5 inline-flex items-center gap-2 text-xs text-foreground/60 hover:text-[#D4AF37]"><ArrowLeft className="w-3.5 h-3.5" />Back to Home</Link>
            </div>
          </aside>

          {/* Main content */}
          <div className="md:col-span-9">
            <article className="prose-wrapper space-y-10">
              {d.body.intro && (
                <p className="text-lg md:text-xl text-foreground/85 leading-relaxed font-serif italic" data-testid="legal-intro">
                  {d.body.intro}
                </p>
              )}
              {d.body.sections.map((s) => <SectionBlock key={s.heading} heading={s.heading} body={s.body} />)}
            </article>

            {/* Contact */}
            <div className="mt-14 p-6 md:p-8 rounded-3xl border border-[#D4AF37]/30 bg-[#F4EFE6] dark:bg-[#0F284B]">
              <div className="eyebrow mb-2">Questions?</div>
              <div className="font-serif text-2xl mb-2">We&rsquo;re here to help.</div>
              <div className="text-sm text-foreground/75">
                Reach us at{" "}
                <a className="text-[#D4AF37] hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>{" "}
                or on{" "}
                <a className="text-[#25D366] hover:underline" href={WA_HREF} target="_blank" rel="noreferrer">WhatsApp (+91 92892 12630)</a>.
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
